import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assertIsDeliverTxSuccess, broadcastTx } from '@/wallet-utils'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { queryKeys } from '@/query-keys'
import { fatal } from '@/utils'
import { MutationParameters } from './types'
import { useSelectedChain } from '@/contexts/wallet'

export interface BroadcastRedeemTokenForsharesMutationReturnType {
  transaction: DeliverTxResponse
}

const useBroadcastRedeemTokenForSharesMutation = ({ redeemTokensRaw }: MutationParameters) => {
  const client = useQueryClient()

  const { getSigningStargateClient } = useSelectedChain()

  const handleMutation = async (): Promise<BroadcastRedeemTokenForsharesMutationReturnType> => {
    if (!redeemTokensRaw) {
      throw fatal('Unable to broadcast empty signed transaction.')
    }

    const client = await getSigningStargateClient()
    const bytes = TxRaw.encode(redeemTokensRaw).finish()
    const transaction = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(transaction)

    return { transaction }
  }

  const handleSettle = async () => {
    await client.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })

    // Make sure that the validators list is updated after the account balances have been updated.
    await client.invalidateQueries({ queryKey: queryKeys.lsmValidatorsBase })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSettled: handleSettle
  })
}

export { useBroadcastRedeemTokenForSharesMutation }
